import React, { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  DocumentIcon,
  FolderIcon,
  CalendarIcon,
  UserIcon,
  EyeIcon,
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { DocumentNode } from '../../types/documents';
import { documentService } from '../../services/documentService';

interface DocumentSearchProps {
  onBackToChat: () => void;
  onOpenDocumentPreview: (document: DocumentNode) => void;
}

export const DocumentSearch: React.FC<DocumentSearchProps> = ({
  onBackToChat,
  onOpenDocumentPreview
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DocumentNode[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [chatMessage, setChatMessage] = useState('');

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const results = await documentService.searchDocuments(searchQuery);
      setSearchResults(results);
    } catch (error) {
      console.error('Erro na busca:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getFileIcon = (nodeType: string) => {
    return nodeType === 'folder' ? (
      <FolderIcon className="h-5 w-5 text-yellow-500" />
    ) : (
      <DocumentIcon className="h-5 w-5 text-blue-500" />
    );
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={onBackToChat}
              className="mr-3 p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Busca de Documentos</h1>
              <p className="text-gray-600">Encontre documentos específicos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Area */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Digite palavras-chave para buscar documentos..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={isSearching || !searchQuery.trim()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSearching ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </div>
      </div>

      {/* Chat Area */}
      <div className="bg-gray-50 border-b border-gray-200 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">Pergunte sobre os documentos encontrados</h3>
          </div>
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Ex: 'Explique o conteúdo do primeiro documento' ou 'Quais são os contratos mais recentes?'"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
              />
            </div>
            <button
              onClick={() => {
                if (chatMessage.trim()) {
                  // Aqui você pode implementar a lógica para enviar a pergunta para o chat
                  setChatMessage('');
                }
              }}
              disabled={!chatMessage.trim()}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Perguntar
            </button>
          </div>
        </div>
      </div>

      {/* Results Area */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Resultados da busca</h2>
                <span className="text-sm text-gray-500">
                  {searchResults.length} documento{searchResults.length !== 1 ? 's' : ''} encontrado{searchResults.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {searchResults.length === 0 ? (
              <div className="p-8 text-center">
                <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">
                  {searchQuery ? `Nenhum resultado encontrado para "${searchQuery}"` : 'Digite uma busca para encontrar documentos'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {searchResults.map((result) => (
                  <div
                    key={result.id}
                    className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => onOpenDocumentPreview(result)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        {getFileIcon(result.nodeType)}
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-gray-900 mb-1">
                            {result.title}
                          </h3>
                          <p className="text-sm text-gray-500 mb-2">
                            {result.path}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-gray-400">
                            <div className="flex items-center">
                              <CalendarIcon className="h-3 w-3 mr-1" />
                              <span>12/01/2024</span>
                            </div>
                            <div className="flex items-center">
                              <UserIcon className="h-3 w-3 mr-1" />
                              <span>João Silva</span>
                            </div>
                            <div className="flex items-center">
                              <DocumentIcon className="h-3 w-3 mr-1" />
                              <span>{result.nodeType}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <EyeIcon className="h-5 w-5 text-blue-600" />
                        <span className="text-sm text-blue-600">Visualizar</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 